import { useAuth } from '@/features/auth/store/authStore';
import { GrowthBook, GrowthBookProvider as Provider } from '@growthbook/growthbook-react';
import { clientEnv } from '@repo/utils';
import type { PropsWithChildren } from 'react';
import { useEffect } from 'react';

const clientKey = clientEnv.VITE_GROWTHBOOK;

export type AppFeatures = {
  'enable-dip': boolean;
  'enable-savings-widget': boolean;
  'maintenance-mode': boolean;
  // TT-137: gates the forced first-login profile-completion redirect for
  // admin-created investors. Off by default. NOTE: the token-schema relaxation
  // (optional birthdate/phone) is always on and independent of this flag — it
  // only stops REJECTING minimal tokens. Operationally this flag must be ON
  // wherever Wakala invites are issued, otherwise an admin-created investor can
  // log in but won't be forced to complete their profile.
  'enable-wakala-profile-completion': boolean;
};

export type AppFeaturesNames = keyof AppFeatures;

export const growthbook = new GrowthBook<AppFeatures>({
  apiHost: 'https://cdn.growthbook.io',
  clientKey,
  enableDevMode: clientEnv.VITE_APP_MODE === 'development',
});

export type Growthbook = typeof growthbook;

export function GrowthbookProvider({ children }: PropsWithChildren) {
  const user = useAuth.use.user();

  useEffect(() => {
    let pfidaDeviceId = localStorage.getItem('pfidaDeviceId');

    if (!pfidaDeviceId) {
      pfidaDeviceId = crypto.randomUUID();
      localStorage.setItem('pfidaDeviceId', pfidaDeviceId);
    }

    growthbook.init({ streaming: true });
    growthbook.setAttributes({ deviceId: pfidaDeviceId, username: user?.['cognito:username'] });
  }, [user]);

  return <Provider growthbook={growthbook}>{children}</Provider>;
}
